<script setup>
import {useConfigurateurStore} from "../../stores/configurateur";
import {ref} from 'vue';
import Logger from "../../utils/logger";
import ConfiguratorCardImage from './ConfiguratorCardImage.vue';

const logger = new Logger('configurateur')

const store = useConfigurateurStore();
const isCollapsed = ref(false);
const showModal = ref(false);
const selectedCircuitForModal = ref(null);

const handleChangeCircuit = (circuitId = null) => {
    // Si un ID est passé et que ce n'est pas un Event, on l'utilise, sinon on utilise celui du store
    const id = (circuitId && typeof circuitId !== 'object') ? circuitId : store.circuit_id;

    store.fetchStep('voiture', {
        circuit_id: id,
        stage_voiture_id: store.voiture_id,
    }, {circuitSelectedByUser: true})
}

const selectCircuit = (circuitId) => {
    logger.log('Carte circuit cliquée pour ID:', circuitId);

    // #136 : on valide la VOITURE courante contre le circuit ciblé (et non l'id de
    // circuit, ce qui réinitialisait à tort la voiture sur la 1re de la liste — Audi R8).
    // On lit le circuit cible directement pour ne pas dépendre d'un store.circuit_id
    // pas encore positionné, et pour rester sûr quand les données ne sont pas chargées
    // (la popin n'a pas eu le temps de charger).
    const targetCircuit = store.circuits.find(circuit => circuit.id == circuitId) || {};
    const voitureValide = Array.isArray(targetCircuit.voiture_ids)
        && targetCircuit.voiture_ids.some(v => v == store.voiture_id);

    if (!voitureValide && store.first_step == 'voiture') {
        logger.log('Le circuit n\'est pas valide pour la voiture');
        return;
    }

    store.circuit_id = circuitId;

    // On ne réinitialise la voiture que si le circuit est réellement chargé
    // (voiture_ids disponibles) et que la voiture courante n'y roule pas — évite de
    // retomber sur la 1re voiture quand les données ne sont pas prêtes (#136).
    if (!voitureValide && targetCircuit.voiture_ids?.length) {
        store.voiture_id = targetCircuit.voiture_ids[0] ?? null;
    }

    handleChangeCircuit(circuitId);
}

const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value;
}

const showCircuitInfo = (circuit, event) => {
    logger.log('Icône info cliquée pour:', circuit.title);
    selectedCircuitForModal.value = circuit;
    showModal.value = true;
}

const closeModal = () => {
    showModal.value = false;
    selectedCircuitForModal.value = null;
}

</script>

<template>
    <section id="step-circuit">
        <div class="card-step-group shadow-sm">
            <div class="step-title d-flex justify-content-between align-items-center">
                <div class="circle"
                     :class="{'active': store.getStepNumber('circuit') == store.step, 'completed': store.isCompleted(store.getStepNumber('circuit'))}">
                    {{ store.getStepNumber('circuit') }}
                </div>
                <span class="h5 large fw-bolder mb-2">{{ $t('Choisissez votre circuit') }}</span>
                <button class="btn btn-sm" type="button" @click="toggleCollapse">
                    <i class="bi" :class="isCollapsed ? 'bi-plus-circle' : 'bi-dash-circle'"></i>
                </button>
            </div>
            <div id="collapse-circuits"
                 class="mb-4 pb-1 card-step d-sm-block card card-step"
                 :class="{ 'show': !isCollapsed, 'collapse': isCollapsed }">

                <div class="card-title">
                    <p class="h3 m-0" v-if="store.circuit">
                        {{ store.circuit.designation + (store.circuit.piste ? ` - ${store.circuit.piste}` : '') }}</p>
                </div>

                <div class="card-body">
                    <div class="row">
                        <div id="list-circuits" class="col-md-12">
                            <div class="accordion-body px-0 row">
                                <div class="col-6 col-xl-6 col-xxxl-4 p-2" v-for="circuit in store.circuitsFiltered"
                                     :key="circuit.id">
                                    <div
                                        class="card card-config card-circuit-config position-relative text-decoration-none"
                                        :class="{
                                            'card-selected': store.circuit_id === circuit.id,
                                            'disabled': !store.circuitValidForVoiture(circuit.id)
                                        }"
                                        style="cursor: pointer;">

                                        <div class="form-check form-switch">
                                            <input @click.stop
                                                   @change="handleChangeCircuit"
                                                   v-model="store.circuit_id" :value="circuit.id"
                                                   class="form-check-input" type="radio"
                                                   name="circuit-product"
                                                   :id="'circuit-product-' + circuit.id">
                                        </div>
                                        <div @click="selectCircuit(circuit.id)" class="card-clickable-area">
                                            <i class="bi bi-info-circle-fill show-infos"
                                               @click.stop.prevent="showCircuitInfo(circuit)"
                                               title="Voir les détails du circuit"></i>
                                            <ConfiguratorCardImage
                                                :src="circuit.image_circuit"
                                                :alt="`${circuit.designation} ${circuit.piste ?? ''}`"
                                                variant="circuit"
                                            />

                                            <div class="card-body">
                                                <div class="card-text">
                                                    <span class="fw-bold">{{ circuit.designation }} {{
                                                            circuit.piste
                                                        }}</span>


                                                </div>
                                                <div class="card-price">
                                                    <div v-if="circuit.price.promo"
                                                         class="badge bg-danger text-white text-center fw-bolder">
                                                        -{{ circuit.price.promo }}
                                                    </div>
                                                    <span class="small fw-normal">{{ $t('À partir de ') }}</span>
                                                    <div class="price d-flex flex-row align-items-baseline">
                                                        <span
                                                            v-if="circuit.price.old_montant_ttc && circuit.price.old_montant_ttc !== circuit.price.montant_ttc"
                                                            class="old-price fw-normal text-decoration-line-through small fs-xxl-6 me-2">
                                                            &nbsp;{{ circuit.price.old_montant_ttc }}€</span>
                                                        <span
                                                            class="text-info price fw-bolder small fs-xl-6 fs-xxl-5 p-lg-0 p-xl-0">
                                                            {{ circuit.price.montant_ttc }}€</span>
                                                        <span class="nb fw-normal small">/{{
                                                                circuit.price.nb_tour_min
                                                            }} tours</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Modal pour les informations du circuit -->
                <div v-if="showModal" class="step-modal-overlay" @click="closeModal">
                    <div class="step-modal-content" @click.stop>
                        <div class="step-modal-header">
                            <h3 class="step-modal-title">
                                {{
                                    selectedCircuitForModal.designation + (selectedCircuitForModal.piste ? ` - ${selectedCircuitForModal.piste}` : '')
                                }}
                            </h3>
                            <button type="button" class="btn-close" @click="closeModal">
                                <i class="bi bi-x-lg"></i>
                            </button>
                        </div>


                        <div class="step-modal-body">
                            <div v-if="selectedCircuitForModal" class="card-circuit-infos">
                                <div class="small fw-normal text-gray-60 mb-3">{{ $t('ADRESSE :') }}
                                    <div v-safe-html="selectedCircuitForModal.info_adresse"></div>
                                </div>
                                <div class="card-text">
                                    <div class="d-flex justify-content-between text-gray-70">
                                        <div class="d-block d-md-flex text-center text-md-start">
                                            <i class="bi bi-speedometer fs-4 me-2"></i>
                                            <div>
                                                <p class="fw-bold small lh-sm mb-0">Vitesse</p>
                                                <p class="fw-bold small lh-sm mb-0">
                                                    {{ selectedCircuitForModal.info_vitesse }}/10</p>
                                            </div>
                                        </div>

                                        <div class="d-block d-md-flex text-center text-md-start">
                                            <i class="bi bi-segmented-nav fs-4 me-2"></i>
                                            <div>
                                                <p class="fw-bold small lh-sm mb-0">Technique</p>
                                                <p class="fw-bold small lh-sm mb-0">
                                                    {{ selectedCircuitForModal.info_technique }}/10</p>
                                            </div>
                                        </div>

                                        <div class="d-block d-md-flex text-center text-md-start">
                                            <i class="bi bi-joystick fs-4 me-2"></i>
                                            <div>
                                                <p class="fw-bold small lh-sm mb-0">Longueur</p>
                                                <p class="fw-bold small lh-sm mb-0">
                                                    {{ selectedCircuitForModal.info_longueur }}KM</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="card-text mt-3" v-if="selectedCircuitForModal.description">
                                    <p v-safe-html="selectedCircuitForModal.description"></p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

</template>
